import { BaseActionObject, StateNodeConfig, assign } from "xstate4"
import { Context, Transitions } from "../../../types"

export const idleState: StateNodeConfig<
  Context,
  {},
  Transitions,
  BaseActionObject
> = {
  on: {
    // TODO: should these break out of reorder mode? Or allow the user to add breaks, boot camp, sections without breaking out of reorder state
    /*
    section Modification Buttons
    */
    ADD_PRACTICE_TEST_SECTION: {
      target: "#StudyPlan.add_section_transition",
      cond: ctx =>
        ctx.studyPlan.meta.canAddPracticeTest === true &&
        ctx.studyPlan.meta.remainingPracticeTestContentSeconds > 0
    },
    ADD_MASTERY_LESSON_SECTION: {
      target: "#StudyPlan.add_section_transition",
      cond: ctx =>
        ctx.studyPlan.meta.canAddMasteryLesson === true &&
        ctx.studyPlan.meta.remainingMasteryContentSeconds > 0
    },
    ADD_BREAK_SECTION: {
      target: "#StudyPlan.add_section_transition"
    },
    TOGGLE_EXPAND_SECTION: {
      actions: assign((ctx, { sectionId }) => ({
        expanded: ctx.expanded.includes(sectionId)
          ? ctx.expanded.filter(id => id !== sectionId)
          : [...ctx.expanded, sectionId]
      }))
    },
    /*
    Buttons that open states that are modal trays,
    some states inside grouping and items
    */
    PLAN_SETTINGS: {
      target: "plan_settings"
    },
    ADD_LESSON_TO_SECTION: {
      target: "add_lesson",
      actions: assign((_ctx, { sectionId }) => ({
        selectedSectionId: sectionId
      }))
    },
    SECTION_SECOND_MODIFIER: {
      target: "modify_section_seconds",
      actions: assign((ctx, { sectionId, targetSectionSeconds }) => ({
        selectedSectionId: sectionId,
        targetSectionSeconds:
          targetSectionSeconds ??
          ctx.studyPlan.sections.find(section => section.id === sectionId)
            ?.targetSeconds
      }))
    },
    VIEW_DETAILS: {
      target: "#StudyPlan.edit_plan.lesson_detail_view",
      actions: assign((_ctx, { sectionId, lessonId }) => ({
        selectedSectionId: sectionId,
        selectedLessonId: lessonId,
        lessonDetailViewModal: true
      }))
    },
    CREATE: {
      target: "#StudyPlan.start_plan"
    },
    OPEN_CONVERT_PLAN_CONFIRMATION: {
      target: "confirm_converting_plan"
    },
    OPEN_VIEW_BREAK: {
      target: "#StudyPlan.view_break",
      actions: assign((_ctx, { sectionId, lessonId, breakType }) => ({
        selectedSectionId: sectionId,
        selectedLessonId: lessonId,
        selectedBreakType: breakType
      }))
    },
    OPEN_FIRST_SAVING_CONFIRMATION: {
      target: "confirm_first_saving"
    },

    /*
    Section or Item hover interaction states without modal trays
    */
    REMOVE_SECTION: {
      target: "remove_section_transition"
    },
    REMOVE_LESSON: {
      target: "#StudyPlan.remove_lesson_transition",
      actions: assign((_ctx, evt) => ({
        selectedLessonId: evt.itemContentId,
        selectedSectionId: evt.sectionId
      }))
    },

    PREVIEW: {
      target: "#StudyPlan.get_lesson_preview_transition"
    },

    /*
    Menu bar button states
    */
    EXPAND_ALL: {
      actions: assign((ctx, _evt) => ({
        expanded: ctx.studyPlan.sections.map(({ id }) => id)
      }))
    },
    COLLAPSE_ALL: {
      actions: assign((_ctx, _evt) => ({ expanded: [] }))
    },
    UNDO: {
      target: "#StudyPlan.undo_transition"
    },
    REDO: {
      target: "#StudyPlan.redo_transition"
    },
    REORDER: {
      target: "reorder_mode"
    },
    TOGGLE_BREAK_BEFORE_SECTION: {
      target: "toggle_break_before_section_transition"
    },
    TOGGLE_BREAK_AFTER_SECTION: {
      target: "toggle_break_after_section_transition"
    },
    TOGGLE_BREAK_BEFORE_LESSON: {
      target: "toggle_break_before_lesson_transition"
    },
    TOGGLE_BREAK_AFTER_LESSON: {
      target: "toggle_break_after_lesson_transition"
    },
    /* Exit edit states */
    SAVE: {
      target: "#StudyPlan.save_study_plan_transition",
      actions: assign((_ctx, evt) => {
        if (evt?.existingPlanOption)
          return {
            existingPlanOption: evt.existingPlanOption
          }

        return {}
      })
    },
    CANCEL: [
      {
        target: "confirm_leave",
        cond: (ctx, _evt) =>
          ctx.studyPlan?.meta?.canUndo || ctx.hasUnsavedStudyPlanChanges
      },
      {
        target: "#StudyPlan.cancel_plan_transition",
        cond: (ctx, _evt) =>
          !ctx.studyPlan?.meta?.canUndo && !ctx.hasUnsavedStudyPlanChanges
      }
    ],
    CHANGE_PROGRAM: [
      {
        // If there are unsaved changes, show confirm modal and store the pending program change
        target: "confirm_leave",
        actions: assign((_ctx, evt) => ({
          pendingProgramChange: {
            program: evt.program,
            subject: evt.subject ?? null
          }
        })),
        cond: (ctx, _evt) =>
          ctx.studyPlan?.meta?.canUndo || ctx.hasUnsavedStudyPlanChanges
      }
      // If no unsaved changes, the global CHANGE_PROGRAM handler will take over
    ],
    LEAVE: [
      {
        target: "confirm_leave",
        actions: assign((_ctx, evt) => {
          return {
            pendingNavigationUrl: evt.location ?? null
          }
        }),
        cond: (ctx, _evt) => {
          const hasUnsaved =
            ctx.studyPlan?.meta?.canUndo || ctx.hasUnsavedStudyPlanChanges
          return hasUnsaved
        }
      }
      // If no unsaved changes, do nothing (navigation will proceed)
    ]
  }
}
